import { create, get, updatePatch } from "./apiService";
import { envConfig } from "../utils/envConfig";

export interface BusinessFeature {
  _id?: string;
  key: string;
  name: string;
  category?: {
    key?: string;
    name?: string;
  };
  platform?: {
    web?: boolean;
    mobile?: boolean;
  };
  status?: string;
}

export interface BusinessProfileBusiness {
  type: "business";
  businessId: string;
  businessName: string;
  logoUrl?: string;
  avatarUrl?: string;
  logo?: string;
  imageUrl?: string;
  roleKey: string;
  roleName: string;
  role?: {
    key: string;
    name: string;
    features?: BusinessFeature[];
  } | null;
  features: BusinessFeature[];
  kybStatus: string;
}

export interface BusinessProfilesResponse {
  personal: {
    type: "personal";
    displayName: string;
    avatarUrl?: string | null;
  };
  activeBusinessId: string | null;
  businesses: BusinessProfileBusiness[];
}

export interface BusinessListItem {
  business?: {
    _id?: string;
    id?: string;
    businessId?: string;
    businessName?: string;
    logo?:
      | {
          url?: string;
        }
      | string
      | null;
    logoUrl?: string | null;
    avatarUrl?: string | null;
    imageUrl?: string | null;
    kybStatus?: string;
    status?: string;
  };
  id?: string;
  _id?: string;
  businessId?: string;
  businessName?: string;
  logo?:
    | {
        url?: string;
      }
    | string
    | null;
  logoUrl?: string | null;
  avatarUrl?: string | null;
  imageUrl?: string | null;
  kybStatus?: string;
  status?: string;
  roleKey?: string;
  roleName?: string;
  role?: {
    key?: string;
    name?: string;
    features?: BusinessFeature[];
  } | null;
  features?: BusinessFeature[];
}

export interface SetActiveBusinessPayload {
  businessId: string | null;
}

export interface SetActiveBusinessResponse {
  activeBusinessId: string | null;
}

export const getBusinessProfiles =
  async (): Promise<BusinessProfilesResponse> => {
    return get<BusinessProfilesResponse>("/api/v2/business/profiles");
  };

export const setActiveBusiness = async (
  payload: SetActiveBusinessPayload
): Promise<SetActiveBusinessResponse> => {
  return updatePatch<SetActiveBusinessResponse>(
    "/api/v2/business/me/active-business",
    {},
    payload
  );
};

export const listBusinesses = async (): Promise<BusinessListItem[]> => {
  return get<BusinessListItem[]>("/api/v2/business");
};

export interface DirectorInviteInput {
  email: string;
  fullName: string;
}

export interface SubmitKybApplicationPayload {
  businessName: string;
  registrationNumber: string;
  contactPhone?: string;
  requesterNotes?: string;
  directorInvites?: DirectorInviteInput[];
}

export interface BusinessAccountRequest {
  _id: string;
  businessName: string;
  registrationNumber: string;
  contactPhone?: string;
  requesterNotes?: string;
  status: string;
  linkedBusinessId?:
    | string
    | {
        _id?: string;
        id?: string;
        businessId?: string;
      }
    | null;
  businessId?: string;
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const submitBusinessAccountRequest = async (
  payload: SubmitKybApplicationPayload
): Promise<BusinessAccountRequest> => {
  return create<BusinessAccountRequest>(
    "/api/v2/business/account-requests",
    payload
  );
};

export const getMyBusinessAccountRequest =
  async (): Promise<BusinessAccountRequest | null> => {
    return get<BusinessAccountRequest>("/api/v2/business/account-requests/me");
  };

export const withdrawBusinessAccountRequest = async (): Promise<void> => {
  await create("/api/v2/business/account-requests/me/withdraw", {});
};

export interface DirectorInviteDetails {
  requestId?: string;
  businessName?: string;
  requestReference?: string;
  requestStatus?: string;
  directorInvite?: {
    email?: string;
    fullName?: string;
    status?: string;
  };
}

export type DirectorConfirmData = DirectorInviteDetails;

export type DirectorInviteAction = "approve" | "decline";

export interface DirectorInviteResponsePayload {
  token: string;
  action: DirectorInviteAction;
}

export const getDirectorInviteByToken = async (
  token: string
): Promise<DirectorInviteDetails> => {
  return getDirectorConfirmData(token);
};

export const linkDirectorInvite = async (token: string): Promise<void> => {
  await getDirectorInviteByToken(token);
};

export const respondToDirectorInvite = async ({
  token,
  action,
}: DirectorInviteResponsePayload): Promise<void> => {
  await confirmDirectorInvite(token, action === "approve");
};

interface DirectorConfirmEnvelope<T> {
  status?: boolean;
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
}

const directorConfirmRequest = async <T>(
  token: string,
  init?: RequestInit
): Promise<T> => {
  const response = await fetch(
    `${envConfig.apiUrl}/api/v2/business/account-requests/director-confirm/${encodeURIComponent(token)}`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      ...init,
    }
  );

  const payload =
    ((await response
      .json()
      .catch(() => null)) as DirectorConfirmEnvelope<T> | null) ?? null;

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        "This director confirmation request is not available."
    );
  }

  const isSuccessful =
    typeof payload?.status === "boolean"
      ? payload.status
      : typeof payload?.success === "boolean"
        ? payload.success
        : true;

  if (!isSuccessful || payload?.data === undefined) {
    if (isSuccessful && response.ok && payload?.data === undefined) {
      return undefined as T;
    }

    throw new Error(
      payload?.message ||
        payload?.error ||
        "This director confirmation request is not available."
    );
  }

  return payload.data;
};

export const getDirectorConfirmData = async (
  token: string
): Promise<DirectorConfirmData> => {
  return directorConfirmRequest<DirectorConfirmData>(token);
};

export const confirmDirectorInvite = async (
  token: string,
  confirmed: boolean
): Promise<void> => {
  await directorConfirmRequest<void>(token, {
    method: "POST",
    body: JSON.stringify({ confirmed }),
  });
};
